import { FilterMetadata } from '@/api/generatedApi';
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useRef, useState } from 'react';
import { getMetadataColumns, getMetadataColumnsFromFilterData } from '@/utils/metadata';
import { useListAllMetadataFields } from './api/metadata/useListAllMetadataFields';

const extractFieldName = (field: string): string => field.replace(/^metadata\./, '');

const getColumnOptions = (canEditMetadata: boolean, filterable?: boolean, onApplyFilter?: () => void) => ({
  editable: canEditMetadata,
  filterable,
  onApplyFilter,
});

export const useColumnVisibility = ({
  metadataFieldNames,
  isLoadingFieldNames,
  canEditMetadata,
  contextKey,
  filterable,
  filterDataMetadata,
  onApplyFilter,
  hasDescriptions = false,
  hasLabels = false,
  metadataFieldsTenantId,
}: {
  metadataFieldNames: string[];
  isLoadingFieldNames: boolean;
  canEditMetadata: boolean;
  contextKey: string;
  filterable?: boolean;
  filterDataMetadata?: FilterMetadata;
  onApplyFilter?: () => void;
  hasDescriptions?: boolean;
  hasLabels?: boolean;
  /** Tenant that owns the metadata fields (defaults to the page tenant). Cross-tenant
   * search passes the active result tab's tenant so column types are correct. */
  metadataFieldsTenantId?: string;
}) => {
  const { metadataFields, isLoading: isLoadingMetadataFields } = useListAllMetadataFields(metadataFieldsTenantId);
  const [userVisibilityState, setUserVisibilityState] = useState<{
    contextKey: string;
    model: GridColumnVisibilityModel;
  }>();
  const isMetadataLoading = isLoadingMetadataFields || isLoadingFieldNames;

  const metadataColumns = useMemo(() => {
    const columnOptions = getColumnOptions(canEditMetadata, filterable, onApplyFilter);
    const allMetadataColumns = getMetadataColumns({ metadataFields, ...columnOptions });
    const filterDataColumns = getMetadataColumnsFromFilterData({
      filterDataMetadata,
      metadataFields,
      ...columnOptions,
    });

    const filterDataFieldNames = new Set(filterDataColumns.map(col => col.field));
    const mergedColumns = [
      ...filterDataColumns,
      ...allMetadataColumns.filter(col => !filterDataFieldNames.has(col.field)),
    ];

    const visibleFieldNames = new Set(metadataFieldNames);
    const [visibleColumns, hiddenColumns] = mergedColumns.reduce<[typeof mergedColumns, typeof mergedColumns]>(
      ([visible, hidden], col) => {
        const fieldName = extractFieldName(col.field);
        return visibleFieldNames.has(fieldName) ? [[...visible, col], hidden] : [visible, [...hidden, col]];
      },
      [[], []]
    );

    return [...visibleColumns, ...hiddenColumns].sort(
      (a, b) => (a?.headerName ?? '').localeCompare(b?.headerName ?? '', undefined, { sensitivity: 'base' }) ?? 0
    );
  }, [metadataFieldNames, metadataFields, canEditMetadata, filterable, filterDataMetadata, onApplyFilter]);

  const derivedVisibilityModel = useMemo(() => {
    const allMetadataColumns = getMetadataColumns({ metadataFields, editable: canEditMetadata });
    const visibleFieldNames = new Set(metadataFieldNames);
    const visibilityModel: Record<string, boolean> = {
      extension: false,
      state: false,
      isAssembly: false,
      isRootAssembly: false,
      description: hasDescriptions,
      label: hasLabels,
    };

    for (const col of allMetadataColumns) {
      if (!visibleFieldNames.has(extractFieldName(col.field))) {
        visibilityModel[col.field] = false;
      }
    }

    if (isMetadataLoading) {
      visibilityModel.creator = false;
      visibilityModel.createdAt = false;
      visibilityModel.folder = false;
    }

    return visibilityModel;
  }, [metadataFieldNames, metadataFields, canEditMetadata, isMetadataLoading, hasDescriptions, hasLabels]);

  const columnVisibilityModel = useMemo(
    () => (userVisibilityState?.contextKey === contextKey ? userVisibilityState.model : derivedVisibilityModel),
    [derivedVisibilityModel, userVisibilityState, contextKey]
  );

  const contextKeyReference = useRef(contextKey);
  contextKeyReference.current = contextKey;

  const onColumnVisibilityModelChange = useCallback(
    (newModel: GridColumnVisibilityModel) =>
      setUserVisibilityState({ contextKey: contextKeyReference.current, model: newModel }),
    []
  );

  return {
    metadataColumns,
    columnVisibilityModel,
    onColumnVisibilityModelChange,
    isMetadataLoading,
  };
};
